/* TELEFONNÍ ČÍSLO PRO VYTÁČENÍ — jedno místo pro wa.me, sms: a tel:.
 *
 * PROČ TENHLE SOUBOR VZNIKL (nález 17. 8. 2026):
 * Na ČTYŘECH místech ležel tenhle řádek, pokaždé zkopírovaný:
 *
 *     if (d.length === 9) d = (lang === "sk" ? "421" : "420") + d;
 *
 *   pages/ReferralPage.js:591 · components/crm/WhatsAppSender.js:25
 *   lib/naborInvite.js:20     · lib/crm/zpravaServis.js:187
 *
 * Předvolba se odvozovala z JAZYKA APLIKACE. Český poradce, který si appku
 * přepnul do slovenštiny (kvůli slovenskému klientovi, nebo jen ze zvědavosti),
 * poslal zprávu na +421 místo +420 — tedy na slovenské číslo, které patří
 * někomu úplně jinému. Zpráva odešla, poradce o tom nevěděl.
 *
 * Je to TÁŽ CHYBA jako měnová (LanguageContext měl `sk: rate 25`): jazyk
 * rozhodoval o datech. Tam to násobilo částky 25×, tady to posílá zprávy
 * cizím lidem.
 *
 * CO PLATÍ TEĎ:
 *   1. Jazyk = v čem jsou NAPSANÉ TEXTY. Nikdy neurčuje předvolbu.
 *   2. Když číslo PŘEDVOLBU UŽ MÁ, vyhrává ona. Klientovo číslo víme jistě,
 *      všechno ostatní jen odhadujeme.
 *   3. Teprve devítimístné číslo BEZ předvolby ji dostane podle ZEMĚ PORADCE
 *      (lib/tenantMena.js) — stejný zdroj pravdy jako měna.
 *   4. Když si nejsme jistí, vrátíme číslo BEZ předvolby. Poradce uvidí, že
 *      něco chybí. Špatná předvolba je horší než žádná — odeslat cizímu
 *      člověku nejde vzít zpět.
 *
 * Pro VALIDACI vstupu (je to platné číslo?) slouží `vPhone` v lib/validators.js.
 * Tenhle modul řeší něco jiného: jak z uloženého čísla udělat odkaz.
 */
import { normZeme, zemeTenanta } from "@/lib/tenantMena";

/** Země → mezinárodní předvolba. Rozšiřuj SPOLU s MENA_ZEME v tenantMena.js. */
export const PREDVOLBA_ZEME = {
  CZ: "420",
  SK: "421",
};

/** Předvolby, které umíme rozpoznat na začátku čísla. */
const ZNAME_PREDVOLBY = Object.values(PREDVOLBA_ZEME);

/* Kratší už není telefon, ať si to poradce zapsal jakkoli. Doporučení ITU
 * (E.164) říká, že mezinárodní číslo má 15 číslic nejvýš; nejkratší reálná
 * čísla ve světě mají kolem sedmi. Šestku bereme jako velkorysou spodní mez.
 *
 * PROČ TO NEPOUŠTÍME DÁL: z „0" nebo „12" by vznikl odkaz `tel:+0`, který
 * vypadá funkčně a při kliknutí neudělá nic. Prázdný návrat je poctivější —
 * volající tlačítko vůbec nenabídne a poradce vidí, že u klienta chybí číslo. */
const NEJKRATSI_CISLO = 6;

/* Horní mez podle téže normy (E.164): mezinárodní číslo má nejvýš 15 číslic.
 *
 * PROČ PŘIBYLA (kontrola 17. 8. 2026): do jednoho pole si poradce občas zapíše
 * DVĚ čísla („777 123 456, 608 111 222"). Po odmazání nečíslic z toho vznikne
 * osmnáctimístný slepenec, který projde jako „číslo, kterému nerozumíme" —
 * a odkaz `wa.me/777123456608111222` vypadá funkčně, ale nevede nikam.
 * To je přesně ten mrtvý odkaz, kterému brání spodní mez; chyběla druhá strana.
 *
 * CO TÍM NEVYŘEŠÍME: číslo s vnitřní linkou („777123456,123" → dvanáct číslic)
 * se od platného mezinárodního čísla odlišit nedá a projde dál. Uhodnout, kde
 * končí číslo a začíná linka, by znamenalo vrátit se k odhadování. */
const NEJDELSI_CISLO = 15;

/** Jen číslice. `+`, mezery, závorky a pomlčky pryč. */
function cislice(vstup) {
  return String(vstup ?? "").replace(/\D/g, "");
}

/**
 * Číslo pro vytáčení — samé číslice včetně mezinárodní předvolby, bez `+`.
 * Takhle ho chce wa.me; pro `sms:` a `tel:` si volající přidá `+` sám.
 *
 * @param {string} telefon  co je uložené u klienta (klidně „+420 777 123 456")
 * @param {string} [zeme]   kód země PORADCE (CZ/SK). Když se neuvede, vezme se
 *                          ze store tenantu. NIKDY nepředávej jazyk.
 * @returns {string} číslo k vytáčení, nebo "" když z toho nejde nic rozumného
 */
export function cisloProVytaceni(telefon, zeme) {
  let d = cislice(telefon);
  if (!d) return "";

  // „00420…" → „420…" (mezinárodní zápis podle staré normy)
  if (d.startsWith("00")) d = d.slice(2);

  // Delší, než norma připouští → není to jedno číslo. Radši nic (viz výše).
  if (d.length > NEJDELSI_CISLO) return "";

  // Předvolbu už má → nesaháme na ni. Klientovo číslo je jistota.
  if (ZNAME_PREDVOLBY.some((p) => d.startsWith(p) && d.length > 9)) return d;

  const zemePoradce = normZeme(zeme ?? zemeTenanta());

  /* Slovenský běžný zápis „0905 123 456" — národní nula + 9 číslic.
     Vyhláška 286/2022 Z. z. § 2 písm. ab): národné rozlišovacie číslo v SR
     je 0; § 6 ods. 4: národní číslo má 9 číslic (slov-lex.sk). Dřív z toho
     vznikl odkaz wa.me/0905123456, který nevede nikam.
     Jen u SK poradce: v ČR národní nula není, u CZ poradce by to byl odhad. */
  if (zemePoradce === "SK" && d.length === 10 && d[0] === "0" && d[1] !== "0") d = d.slice(1);

  // Devítimístné bez předvolby → doplníme podle ZEMĚ PORADCE.
  if (d.length === 9) {
    const kod = PREDVOLBA_ZEME[zemePoradce];
    return kod ? kod + d : d;
  }

  /* Cokoli jiného (7 číslic, 15 číslic, cizí předvolba) necháme, jak je.
     Nevíme, co to je — a odhadovat předvolbu u čísla, kterému nerozumíme,
     je přesně ta chyba, kvůli které tenhle soubor vznikl.
     Pod hranicí NEJKRATSI_CISLO ale nejde o číslo vůbec → radši nic. */
  return d.length >= NEJKRATSI_CISLO ? d : "";
}

/**
 * Odkaz na WhatsApp. Vrací "" když číslo nedává smysl — volající pak
 * tlačítko vůbec nenabídne (lepší než odkaz, který nikam nevede).
 */
export function odkazWhatsApp(telefon, text = "", zeme) {
  const cislo = cisloProVytaceni(telefon, zeme);
  if (!cislo) return "";
  const q = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${cislo}${q}`;
}

/**
 * Odkaz na SMS. Na iOS se tělo zprávy odděluje `&`, jinde `?` — proto
 * `oddelovac`. Zpráva se NEODESÍLÁ, jen se otevře SMS aplikace
 * s předvyplněným textem a poradce v ní klikne Odeslat.
 */
export function odkazSms(telefon, text = "", zeme, { ios = false } = {}) {
  const cislo = cisloProVytaceni(telefon, zeme);
  if (!cislo) return "";
  if (!text) return `sms:+${cislo}`;
  const oddelovac = ios ? "&" : "?";
  return `sms:+${cislo}${oddelovac}body=${encodeURIComponent(text)}`;
}

/**
 * Apple zařízení (iPhone, iPad, Mac — Messages) chtějí tělo SMS za `&`,
 * ostatní za `?` → `odkazSms(…, { ios: jeAppleZarizeni() })`.
 * JEDNA detekce pro celou appku (kontrola 14. 10. 2026): dřív ji měla řada
 * „Co má dodat klient“ i řada doporučení každá jinak a na Macu skládaly SMS
 * každá s jiným oddělovačem. iPadOS v desktopovém režimu hlásí „Mac OS X“.
 */
export function jeAppleZarizeni() {
  try {
    const n = typeof navigator !== "undefined" ? navigator : null;
    if (!n) return false;
    const ua = String(n.userAgent || "");
    return /iPad|iPhone|iPod|Mac OS X/.test(ua) || (n.platform === "MacIntel" && n.maxTouchPoints > 1);
  } catch {
    return false;
  }
}

/** Odkaz na volání. */
export function odkazTel(telefon, zeme) {
  const cislo = cisloProVytaceni(telefon, zeme);
  return cislo ? `tel:+${cislo}` : "";
}
